'use client'

import { MouseEventHandler, useCallback } from 'react'

import { stripLocaleFromPathname } from '../../lib/routing'

type Options = {
  href: string
  target?: string
  onClick?: MouseEventHandler<HTMLElement>
}

const normalizePathname = (pathname: string): string =>
  stripLocaleFromPathname(pathname).replace(/\/+$/, '') || '/'

const isCurrentUrl = (href: string): boolean => {
  try {
    const destination = new URL(href, window.location.href)
    return (
      destination.origin === window.location.origin &&
      normalizePathname(destination.pathname) === normalizePathname(window.location.pathname) &&
      destination.search === window.location.search &&
      destination.hash === window.location.hash
    )
  } catch {
    return false // Invalid URL
  }
}

export function useReloadOnCurrentRouteClick({
  href,
  target,
  onClick,
}: Options): MouseEventHandler<HTMLElement> {
  return useCallback(
    event => {
      onClick?.(event)
      if (event.defaultPrevented) return

      // Don't reload when a user is trying to open link in new window/tab
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return
      }

      if (target?.toLowerCase() === '_blank') return

      if (isCurrentUrl(href)) {
        event.preventDefault()
        window.location.reload()
      }
    },
    [href, target, onClick]
  )
}
